/**
 * Types et constantes standardisés pour les statuts de réservation
 *
 * Single source of truth côté client — doit rester aligné avec
 * `create_booking_lock` / convert_lock (statuts qui bloquent le calendrier).
 */

import { BookingStatus } from "@/types/booking";

/**
 * Statuts qui bloquent la disponibilité (calendrier + checkAvailability).
 * Aligné sur create_booking_lock :
 * pending | confirmed | in_progress | ready_for_checkin | ready_for_checkout
 * | pending_docs | awaiting_verification | approved
 */
export const BLOCKING_BOOKING_STATUSES: readonly BookingStatus[] = [
  "pending",
  "confirmed",
  "in_progress",
  "ready_for_checkin",
  "ready_for_checkout",
  "pending_docs",
  "awaiting_verification",
  "approved",
] as const;

/** Vérifie si un statut bloque la disponibilité */
export const isBlockingStatus = (status: BookingStatus): boolean =>
  (BLOCKING_BOOKING_STATUSES as readonly string[]).includes(status);

/**
 * Statuts qui ne bloquent PAS la disponibilité
 */
export const NON_BLOCKING_BOOKING_STATUSES: readonly BookingStatus[] = [
  "completed",
  "cancelled",
  "rejected",
  "auto_cancelled",
  "needs_review",
] as const;

export type BlockingBookingStatus = (typeof BLOCKING_BOOKING_STATUSES)[number];

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: "En attente",
  confirmed: "Confirmée",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
  rejected: "Refusée",
  ready_for_checkin: "Prête pour check-in",
  ready_for_checkout: "Prête pour check-out",
  pending_docs: "Documents en attente",
  awaiting_verification: "En vérification",
  approved: "Approuvée",
  auto_cancelled: "Annulée automatiquement",
  needs_review: "À revoir",
};

export const BOOKING_STATUS_COLORS: Record<BookingStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-green-100 text-green-800 border-green-200",
  completed: "bg-gray-100 text-gray-800 border-gray-200",
  cancelled: "bg-gray-100 text-gray-800 border-gray-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  ready_for_checkin: "bg-green-100 text-green-800 border-green-200",
  ready_for_checkout: "bg-blue-100 text-blue-800 border-blue-200",
  pending_docs: "bg-amber-100 text-amber-800 border-amber-200",
  awaiting_verification: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-sky-100 text-sky-800 border-sky-200",
  auto_cancelled: "bg-gray-100 text-gray-700 border-gray-200",
  needs_review: "bg-orange-100 text-orange-800 border-orange-200",
};
